/**
 * Branchement SSO Chat sur suite-shared (avant / avec le shell React).
 * CLEAR_CACHE hub et cookie aurelia_purge → installChatCacheGuard (un seul rechargement).
 */
import { getSuiteHubWhoamiUrl, isChatStandalone } from "./apiBase";
import { isChatGuestJoin } from "./lib/guestToken";
import { installChatCacheGuard } from "./lib/cache/chatSwBootstrap";
import { isChatHub2Desk } from "./chatHub2";
import { announceChatHub2User, isChatEmbeddedInHub, readStoredSuiteUserId } from "./suiteSession";

function bindChatReconnect(): void {
  const btn = document.getElementById("btnOpenAuthPopup");
  if (!btn || btn.dataset.aureliaReconnect === "1") return;
  const fresh = btn.cloneNode(true) as HTMLButtonElement;
  fresh.dataset.aureliaReconnect = "1";
  btn.replaceWith(fresh);
  fresh.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Le clone retire le handler suite-shared qui remet suppressAutoAuthDelivery=false.
    window.enableSuiteAuthAutoReconnect?.();
    if (window.parent !== window && window.getSuiteAuthPopupUrl) {
      const popup = window.open(
        window.getSuiteAuthPopupUrl("chat"),
        "Aurélia Suite - Connexion",
        "width=520,height=680,scrollbars=yes,resizable=yes",
      );
      if (popup) return;
    }
    window.openSuiteAuthPopup?.("chat");
  });
}

function mountChatSharedModals(): void {
  if (typeof window.suiteAuthModalMount === "function") {
    window.suiteAuthModalMount({
      appName: "Chat",
    });
  }
  if (typeof window.suiteForbiddenModalMount === "function") {
    window.suiteForbiddenModalMount({ appName: "Chat" });
  }
}

function runChatSharedInit(): void {
  if (isChatGuestJoin()) return;
  if (isChatHub2Desk() || isChatStandalone()) {
    mountChatSharedModals();
    installChatCacheGuard();
    if (typeof window.installSuiteAuthPopup === "function") {
      window.installSuiteAuthPopup({
        redirect: "chat",
        isConnected: () => window.__chatIsConnected?.() ?? false,
        onAuthenticated: async (userId) => {
          await window.__chatOnAuthenticated?.(userId);
        },
        onSessionLost: () => {
          window.__chatOnSessionLost?.(null);
        },
        patchFetch: false,
      });
      bindChatReconnect();
    }
    // Hub 2 : App.tsx fait le whoami+ping une seule fois (évite double « Vérification… »).
    if (!isChatHub2Desk()) {
      void announceChatHub2User();
    }
    return;
  }
  mountChatSharedModals();
  installChatCacheGuard();

  if (typeof window.installSuiteAuthPopup !== "function") {
    console.error("Chat : kit suite-shared introuvable (installSuiteAuthPopup)");
    return;
  }

  window.installSuiteAuthPopup({
    redirect: "chat",
    isConnected: () => window.__chatIsConnected?.() ?? false,
    onAuthenticated: async (userId) => {
      await window.__chatOnAuthenticated?.(userId);
    },
    onSessionLost: () => {
      window.__chatOnSessionLost?.(null);
    },
    patchFetch: false,
  });

  bindChatReconnect();

  // Comme Mail : pas de poll whoami en PWA standalone.
  if (isChatStandalone()) {
    return;
  }

  const whoamiUrl = getSuiteHubWhoamiUrl();
  if (whoamiUrl && typeof window.installSuiteHubSessionWatch === "function") {
    window.installSuiteHubSessionWatch({
      whoamiUrl,
      isEmbeddedInHub: isChatEmbeddedInHub,
      hasStoredIdentity: () => Boolean(readStoredSuiteUserId()),
      isAppSessionActive: () => window.__chatSessionVerified === true,
      onSessionLost: () => {
        window.__chatOnSessionLost?.(null);
      },
    });
  }
}

if (window.__SUITE_SHARED_READY__) {
  runChatSharedInit();
} else {
  document.addEventListener("suiteSharedReady", runChatSharedInit, { once: true });
  document.addEventListener("suiteSharedFailed", () => {
    console.error("Chat : échec chargement suite-shared");
  });
}
